// ─── Supported Languages ──────────────────────────────────────────────────────
export type Language = 'en' | 'ar' | 'so' | 'ur' | 'fr' | 'sw' | 'id' | 'ms' | 'tr' | 'hi' | 'bn';

export const SUPPORTED_LANGUAGES: Array<{ code: Language; label: string; nativeLabel: string; dir: 'ltr' | 'rtl'; flag: string }> = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr', flag: '🇬🇧' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'so', label: 'Somali', nativeLabel: 'Soomaali', dir: 'ltr', flag: '🇸🇴' },
  { code: 'ur', label: 'Urdu', nativeLabel: 'اردو', dir: 'rtl', flag: '🇵🇰' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', dir: 'ltr', flag: '🇫🇷' },
  { code: 'sw', label: 'Swahili', nativeLabel: 'Kiswahili', dir: 'ltr', flag: '🇰🇪' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia', dir: 'ltr', flag: '🇮🇩' },
  { code: 'ms', label: 'Malay', nativeLabel: 'Bahasa Melayu', dir: 'ltr', flag: '🇲🇾' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe', dir: 'ltr', flag: '🇹🇷' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা', dir: 'ltr', flag: '🇧🇩' },
];

// ─── Translation Map ──────────────────────────────────────────────────────────
export interface Translations {
  // Nav
  nav_features: string;
  nav_pricing: string;
  nav_all_plans: string;
  nav_community: string;
  nav_ambassadors: string;
  nav_consultants: string;
  nav_sign_in: string;
  nav_get_started: string;

  // Hero
  hero_badge: string;
  hero_headline_1: string;
  hero_headline_highlight: string;
  hero_headline_2: string;
  hero_subheadline: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  hero_download_label: string;
  hero_trust_encrypted: string;
  hero_trust_languages: string;
  hero_trust_islamic: string;
  hero_trust_voice: string;
  hero_explore: string;

  // Features section
  features_label: string;
  features_heading_1: string;
  features_heading_2: string;
  features_subtext: string;

  // Pricing section
  pricing_label: string;
  pricing_heading: string;
  pricing_subtext: string;
  pricing_usd_note: string;
  pricing_regional_label: string;
  pricing_regional_heading: string;
  pricing_regional_subtext: string;
  pricing_pay_here: string;
  pricing_no_card: string;
  pricing_ppp_note: string;
  pricing_then_choose: string;

  // Community section
  community_label: string;
  community_heading_1: string;
  community_heading_2: string;
  community_subtext: string;
  community_cta: string;
  community_stat_languages: string;
  community_stat_pillars: string;
  community_stat_growth: string;
  community_stat_privacy: string;

  // Consultants section
  consultants_label: string;
  consultants_heading_1: string;
  consultants_heading_2: string;
  consultants_subtext: string;
  consultants_cta_browse: string;
  consultants_cta_become: string;
  consultants_featured_label: string;
  consultants_featured_heading: string;
  consultants_available: string;
  consultants_busy: string;
  consultants_sessions: string;
  consultants_view_all: string;

  // Ambassadors section
  ambassadors_label: string;
  ambassadors_heading_1: string;
  ambassadors_heading_2: string;
  ambassadors_subtext: string;
  ambassadors_commission: string;
  ambassadors_referrals: string;
  ambassadors_cta_apply: string;
  ambassadors_cta_learn: string;
  ambassadors_ready_heading: string;
  ambassadors_ready_subtext: string;

  // Seniors section
  seniors_label: string;
  seniors_heading_1: string;
  seniors_heading_2: string;
  seniors_subtext: string;
  seniors_cta_tour: string;
  seniors_cta_browse: string;
  seniors_featured_label: string;
  seniors_featured_heading: string;
  seniors_available: string;
  seniors_busy: string;
  seniors_answers: string;
  seniors_take_tour: string;

  // Onboarding CTA
  onboarding_label: string;
  onboarding_heading: string;
  onboarding_subtext: string;
  onboarding_cta_create: string;
  onboarding_cta_signin: string;
  onboarding_privacy_note: string;

  // Footer
  footer_features: string;
  footer_pricing: string;
  footer_ambassadors: string;
  footer_signin: string;

  // Language switcher
  lang_switcher_label: string;
  lang_reading_in: string;
}

const translations: Record<Language, Translations> = {
  en: {
    nav_features: 'Features',
    nav_pricing: 'Pricing',
    nav_all_plans: 'All Plans',
    nav_community: 'Community',
    nav_ambassadors: 'Ambassadors',
    nav_consultants: 'Consultants',
    nav_sign_in: 'Sign in',
    nav_get_started: 'Get Started',
    hero_badge: 'AI Islamic Personal Assistant & Growth Ecosystem',
    hero_headline_1: 'Your AI companion for',
    hero_headline_highlight: 'daily growth',
    hero_headline_2: 'rooted in Islamic wisdom',
    hero_subheadline: 'Rafeeq is a UK based tech company aiming to serve global Muslims wherever they are — blending positive psychology, Quranic principles, and real-time AI coaching to help Muslim students, graduates, and professionals build consistent, purposeful lives in their own language.',
    hero_cta_primary: 'Begin Your Journey',
    hero_cta_secondary: 'View Pricing',
    hero_download_label: 'Download the app:',
    hero_trust_encrypted: 'End-to-end encrypted',
    hero_trust_languages: '10 languages supported',
    hero_trust_islamic: 'Islamic-first design',
    hero_trust_voice: 'Real-time voice AI',
    hero_explore: 'Explore',
    features_label: 'Core Pillars',
    features_heading_1: 'Built for the whole you —',
    features_heading_2: 'mind, career, and spirit',
    features_subtext: 'Six integrated modules that work together to support every dimension of a Muslim professional\'s life — from daily habits to career breakthroughs.',
    pricing_label: 'Transparent Pricing',
    pricing_heading: 'One platform, three paths',
    pricing_subtext: 'Priced for where you are in life — with purchasing power parity applied automatically based on your region. Pay via mobile money in your local currency. No hidden fees, cancel anytime.',
    pricing_usd_note: 'USD reference price — pay in your local currency below',
    pricing_regional_label: 'Pay with Mobile Money',
    pricing_regional_heading: 'Select your region to pay locally',
    pricing_regional_subtext: 'All payments are processed through trusted local mobile money providers — no card required.',
    pricing_pay_here: 'Pay here',
    pricing_no_card: '💳 Card checkout is not available — all payments are routed through regional mobile money for lower fees and better accessibility.',
    pricing_ppp_note: '🌍 Local pricing available for Kenya (KES), Uganda (UGX), Somalia (SOS), Indonesia (IDR), Pakistan (PKR), and Malaysia (MYR).',
    pricing_then_choose: 'Then choose your regional payment below ↓',
    community_label: 'Global Community',
    community_heading_1: 'For the Muslim diaspora,',
    community_heading_2: 'wherever you are',
    community_subtext: 'Rafeeq was built with Somali, Arab, South Asian, and Southeast Asian communities in mind — using universal Islamic concepts accepted across all schools of thought. No cultural gatekeeping, just genuine support.',
    community_cta: 'Join the community',
    community_stat_languages: 'Languages supported',
    community_stat_pillars: 'AI coaching pillars',
    community_stat_growth: 'Daily growth potential',
    community_stat_privacy: 'Privacy-first design',
    consultants_label: '1-on-1 Consultants',
    consultants_heading_1: 'Book a session with a',
    consultants_heading_2: 'Muslim professional coach',
    consultants_subtext: 'Our vetted freelance consultants offer personalised 1-on-1 video sessions covering career, business, mental wellness, and spiritual growth — all from a faith-conscious perspective.',
    consultants_cta_browse: 'Browse All Consultants',
    consultants_cta_become: 'Become a Consultant',
    consultants_featured_label: 'Featured Consultants',
    consultants_featured_heading: 'Available for booking now',
    consultants_available: 'Available',
    consultants_busy: 'Busy',
    consultants_sessions: 'sessions',
    consultants_view_all: 'View All →',
    ambassadors_label: 'Ambassador Program',
    ambassadors_heading_1: 'Earn while you spread',
    ambassadors_heading_2: 'goodness in your community',
    ambassadors_subtext: 'Share Rafeeq with your network and earn recurring commissions on every subscription you refer. The more you grow, the higher your tier — and your earnings.',
    ambassadors_commission: 'commission',
    ambassadors_referrals: 'referrals',
    ambassadors_cta_apply: 'Apply Now',
    ambassadors_cta_learn: 'Learn More',
    ambassadors_ready_heading: 'Ready to become an Ambassador?',
    ambassadors_ready_subtext: 'Apply in minutes. No minimum followers required — just a genuine passion for helping your community grow with Rafeeq.',
    seniors_label: 'For Seniors',
    seniors_heading_1: 'Ask a verified scholar.',
    seniors_heading_2: 'Get a personal answer.',
    seniors_subtext: 'Our Scholar Directory connects you with verified Islamic scholars who respond personally to your questions — with the patience, respect, and depth that seniors deserve.',
    seniors_cta_tour: 'Take the Interactive Tour',
    seniors_cta_browse: 'Browse Scholar Directory',
    seniors_featured_label: 'Featured Scholars',
    seniors_featured_heading: 'Meet some of our verified scholars',
    seniors_available: 'Available',
    seniors_busy: 'Busy',
    seniors_answers: 'answers',
    seniors_take_tour: 'Take the Tour',
    onboarding_label: 'Secure Onboarding',
    onboarding_heading: 'Start your journey today. Your data stays yours.',
    onboarding_subtext: 'Create your account in under 2 minutes. We collect only what we need to personalize your AI coaching experience — and you can delete everything, anytime.',
    onboarding_cta_create: 'Create Free Account',
    onboarding_cta_signin: 'Sign in to dashboard',
    onboarding_privacy_note: '🔐 WebAuthn passkey login available · GDPR compliant · Zero-knowledge privacy',
    footer_features: 'Features',
    footer_pricing: 'Pricing',
    footer_ambassadors: 'Ambassadors',
    footer_signin: 'Sign In',
    lang_switcher_label: 'Language',
    lang_reading_in: 'Reading in',
  },

  ar: {
    nav_features: 'المميزات',
    nav_pricing: 'الأسعار',
    nav_all_plans: 'جميع الخطط',
    nav_community: 'المجتمع',
    nav_ambassadors: 'السفراء',
    nav_consultants: 'المستشارون',
    nav_sign_in: 'تسجيل الدخول',
    nav_get_started: 'ابدأ الآن',
    hero_badge: 'مساعد إسلامي شخصي بالذكاء الاصطناعي ونظام بيئي للنمو',
    hero_headline_1: 'رفيقك الذكي من أجل',
    hero_headline_highlight: 'النمو اليومي',
    hero_headline_2: 'المتجذر في الحكمة الإسلامية',
    hero_subheadline: 'رفيق شركة تقنية بريطانية تهدف إلى خدمة المسلمين في جميع أنحاء العالم — تجمع بين علم النفس الإيجابي والمبادئ القرآنية والتدريب الفوري بالذكاء الاصطناعي لمساعدة الطلاب والخريجين والمهنيين المسلمين على بناء حياة هادفة ومنتظمة بلغتهم الخاصة.',
    hero_cta_primary: 'ابدأ رحلتك',
    hero_cta_secondary: 'عرض الأسعار',
    hero_download_label: 'حمّل التطبيق:',
    hero_trust_encrypted: 'تشفير من طرف إلى طرف',
    hero_trust_languages: 'يدعم 10 لغات',
    hero_trust_islamic: 'تصميم إسلامي أولاً',
    hero_trust_voice: 'ذكاء اصطناعي صوتي فوري',
    hero_explore: 'استكشف',
    features_label: 'الأعمدة الأساسية',
    features_heading_1: 'مبني لك بالكامل —',
    features_heading_2: 'العقل والمهنة والروح',
    features_subtext: 'ستة وحدات متكاملة تعمل معاً لدعم كل جانب من جوانب حياة المسلم المهني — من العادات اليومية إلى الاختراقات المهنية.',
    pricing_label: 'أسعار شفافة',
    pricing_heading: 'منصة واحدة، ثلاثة مسارات',
    pricing_subtext: 'أسعار مناسبة لمرحلتك في الحياة — مع تطبيق تعادل القوة الشرائية تلقائياً بناءً على منطقتك. ادفع عبر المحافظ المحلية. لا رسوم خفية، إلغاء في أي وقت.',
    pricing_usd_note: 'سعر مرجعي بالدولار — ادفع بعملتك المحلية أدناه',
    pricing_regional_label: 'ادفع بالمحفظة المحلية',
    pricing_regional_heading: 'اختر منطقتك للدفع محلياً',
    pricing_regional_subtext: 'تتم معالجة جميع المدفوعات عبر مزودي المحافظ المحلية الموثوقين — لا بطاقة مطلوبة.',
    pricing_pay_here: 'ادفع هنا',
    pricing_no_card: '💳 الدفع بالبطاقة غير متاح — جميع المدفوعات تتم عبر المحافظ المحلية لرسوم أقل وإمكانية وصول أفضل.',
    pricing_ppp_note: '🌍 الأسعار المحلية متاحة لكينيا وأوغندا والصومال وإندونيسيا وباكستان وماليزيا.',
    pricing_then_choose: 'ثم اختر طريقة الدفع الإقليمية أدناه ↓',
    community_label: 'مجتمع عالمي',
    community_heading_1: 'للمسلمين في الشتات،',
    community_heading_2: 'أينما كنتم',
    community_subtext: 'بُني رفيق مع مراعاة مجتمعات الصومال والعرب وجنوب آسيا وجنوب شرق آسيا — باستخدام مفاهيم إسلامية عالمية مقبولة عبر جميع المذاهب. لا حواجز ثقافية، فقط دعم حقيقي.',
    community_cta: 'انضم إلى المجتمع',
    community_stat_languages: 'لغة مدعومة',
    community_stat_pillars: 'أعمدة التدريب بالذكاء الاصطناعي',
    community_stat_growth: 'إمكانية النمو اليومي',
    community_stat_privacy: 'تصميم يضع الخصوصية أولاً',
    consultants_label: 'مستشارون فردي',
    consultants_heading_1: 'احجز جلسة مع',
    consultants_heading_2: 'مدرب مسلم محترف',
    consultants_subtext: 'يقدم مستشارونا المستقلون المعتمدون جلسات فيديو فردية مخصصة تغطي المهنة والأعمال والصحة النفسية والنمو الروحي — كل ذلك من منظور إيماني.',
    consultants_cta_browse: 'تصفح جميع المستشارين',
    consultants_cta_become: 'كن مستشاراً',
    consultants_featured_label: 'مستشارون مميزون',
    consultants_featured_heading: 'متاحون للحجز الآن',
    consultants_available: 'متاح',
    consultants_busy: 'مشغول',
    consultants_sessions: 'جلسة',
    consultants_view_all: 'عرض الكل →',
    ambassadors_label: 'برنامج السفراء',
    ambassadors_heading_1: 'اكسب بينما تنشر',
    ambassadors_heading_2: 'الخير في مجتمعك',
    ambassadors_subtext: 'شارك رفيق مع شبكتك واكسب عمولات متكررة على كل اشتراك تحيل إليه. كلما نمت، ارتفع مستواك — وأرباحك.',
    ambassadors_commission: 'عمولة',
    ambassadors_referrals: 'إحالة',
    ambassadors_cta_apply: 'قدّم الآن',
    ambassadors_cta_learn: 'اعرف المزيد',
    ambassadors_ready_heading: 'هل أنت مستعد لتصبح سفيراً؟',
    ambassadors_ready_subtext: 'قدّم في دقائق. لا يشترط حد أدنى من المتابعين — فقط شغف حقيقي بمساعدة مجتمعك على النمو مع رفيق.',
    seniors_label: 'لكبار السن',
    seniors_heading_1: 'اسأل عالماً موثقاً.',
    seniors_heading_2: 'احصل على إجابة شخصية.',
    seniors_subtext: 'يربطك دليل العلماء بعلماء إسلاميين موثقين يردون شخصياً على أسئلتك — بالصبر والاحترام والعمق الذي يستحقه كبار السن.',
    seniors_cta_tour: 'خذ الجولة التفاعلية',
    seniors_cta_browse: 'تصفح دليل العلماء',
    seniors_featured_label: 'علماء مميزون',
    seniors_featured_heading: 'تعرف على بعض علمائنا الموثقين',
    seniors_available: 'متاح',
    seniors_busy: 'مشغول',
    seniors_answers: 'إجابة',
    seniors_take_tour: 'خذ الجولة',
    onboarding_label: 'تسجيل آمن',
    onboarding_heading: 'ابدأ رحلتك اليوم. بياناتك تبقى ملكك.',
    onboarding_subtext: 'أنشئ حسابك في أقل من دقيقتين. نجمع فقط ما نحتاجه لتخصيص تجربة التدريب بالذكاء الاصطناعي — ويمكنك حذف كل شيء في أي وقت.',
    onboarding_cta_create: 'إنشاء حساب مجاني',
    onboarding_cta_signin: 'تسجيل الدخول إلى لوحة التحكم',
    onboarding_privacy_note: '🔐 تسجيل دخول بمفتاح WebAuthn متاح · متوافق مع GDPR · خصوصية بدون معرفة',
    footer_features: 'المميزات',
    footer_pricing: 'الأسعار',
    footer_ambassadors: 'السفراء',
    footer_signin: 'تسجيل الدخول',
    lang_switcher_label: 'اللغة',
    lang_reading_in: 'تقرأ بـ',
  },

  so: {
    nav_features: 'Astaamaha',
    nav_pricing: 'Qiimaha',
    nav_all_plans: 'Dhammaan Qorshayaasha',
    nav_community: 'Bulshada',
    nav_ambassadors: 'Safiirrada',
    nav_consultants: 'La-taliyayaasha',
    nav_sign_in: 'Gal',
    nav_get_started: 'Bilow',
    hero_badge: 'Kaaliyaha Shakhsiga Islaamiga ah ee AI & Nidaamka Kobaca',
    hero_headline_1: 'Saaxiibkaaga AI ee',
    hero_headline_highlight: 'koboca maalinlaha ah',
    hero_headline_2: 'ee ku xidhan xigmadda Islaamiga ah',
    hero_subheadline: 'Rafeeq waa shirkad teknoolojiyeed oo ku salaysan UK oo ujeedadeeda tahay inay adeegto Muslimiinta adduunka meel kasta oo ay joogaan — iyadoo isku dareysa cilmiga nafsiga ee togan, mabaadi\'da Quraanka, iyo tababarka AI ee xilli-waqtiga ah si ay u caawiso ardayda Muslimka ah, qalin-jabiyayaasha, iyo xirfadlayaasha inay dhisaan nolosha joogtada ah ee ujeedada leh luqaddooda.',
    hero_cta_primary: 'Bilow Safarkaga',
    hero_cta_secondary: 'Arag Qiimaha',
    hero_download_label: 'Soo deji abka:',
    hero_trust_encrypted: 'Sirta dhamaadka ilaa dhamaadka',
    hero_trust_languages: '10 luqadood oo la taageero',
    hero_trust_islamic: 'Naqshad Islaamiga ah marka hore',
    hero_trust_voice: 'AI codka xilli-waqtiga ah',
    hero_explore: 'Baadh',
    features_label: 'Tiirarka Aasaasiga ah',
    features_heading_1: 'Loogu dhisay adiga oo dhan —',
    features_heading_2: 'maskaxda, xirfadda, iyo ruuxda',
    features_subtext: 'Lix qaybood oo isku dhafan oo wada shaqeeya si ay u taageeraan dhammaan dhinacyada nolosha xirfadlaha Muslimka ah — laga bilaabo caadooyinka maalinlaha ah ilaa guulaha xirfadeed.',
    pricing_label: 'Qiimaha Daahfurka ah',
    pricing_heading: 'Hal platform, saddex jid',
    pricing_subtext: 'Qiimaha ku habboon halka aad ku jirto nolosha — iyadoo la isticmaalayo xoogga iibsiga ee gobolka si toos ah. Ku bixi lacagta mobilka ee luqaddaada. Maaha khidmado qarsoodi ah, jooji mar kasta.',
    pricing_usd_note: 'Qiimaha tixraaca USD — ku bixi lacagta maxalliga ah ee hoose',
    pricing_regional_label: 'Ku bixi Lacagta Mobilka',
    pricing_regional_heading: 'Dooro gobolkaaga si aad u bixiso maxalliga ah',
    pricing_regional_subtext: 'Dhammaan lacag-bixinta waxaa lagu hawlgeliyaa adeegayaasha lacagta mobilka ee maxalliga ah ee la aaminsan — kaadhka looma baahna.',
    pricing_pay_here: 'Halkan ku bixi',
    pricing_no_card: '💳 Bixinta kaadhka ma heli karto — dhammaan lacag-bixinta waxaa loo gudbiyaa lacagta mobilka ee gobolka si khidmadda ay u jirto oo hufan.',
    pricing_ppp_note: '🌍 Qiimaha maxalliga ah wuxuu u diyaar yahay Kenya, Uganda, Soomaaliya, Indonesia, Pakistan, iyo Malaysia.',
    pricing_then_choose: 'Kadib dooro habka lacag-bixinta gobolka ee hoose ↓',
    community_label: 'Bulshada Caalamiga ah',
    community_heading_1: 'Muslimiinta Diaspora-da,',
    community_heading_2: 'meel kasta oo aad joogto',
    community_subtext: 'Rafeeq waxaa loo dhisay isagoo xasuusanaya bulshada Soomaalida, Carabta, Koonfurta Aasiya, iyo Koonfur-bari Aasiya — isticmaalaya fikradaha Islaamiga ee caalamiga ah ee lagu aqbalay dhammaan maktabadaha fikirka. Xidid dhaqameed ma jirto, taageero run ah oo kaliya.',
    community_cta: 'Ku biir bulshada',
    community_stat_languages: 'Luqadood la taageero',
    community_stat_pillars: 'Tiirarka tababarka AI',
    community_stat_growth: 'Awoodda koboca maalinlaha ah',
    community_stat_privacy: 'Naqshad xoriyad-marka hore',
    consultants_label: 'La-taliyayaasha 1-ka-1',
    consultants_heading_1: 'Buuxi fadhiga',
    consultants_heading_2: 'tababaraha xirfadlaha Muslimka ah',
    consultants_subtext: 'La-taliyayaasheena madax-banaan ee la xaqiijiyay waxay bixiyaan fadhiyo fiidiyow shakhsi ah oo 1-ka-1 ah oo daboolaya xirfadda, ganacsiga, caafimaadka maskaxda, iyo koboca ruuxiga — oo dhan ka soo jeeda aragti rumaysad-xisaab.',
    consultants_cta_browse: 'Raadi Dhammaan La-taliyayaasha',
    consultants_cta_become: 'Noqo La-taliye',
    consultants_featured_label: 'La-taliyayaasha Muuqda',
    consultants_featured_heading: 'Diyaar u ah buuxinta hadda',
    consultants_available: 'Diyaar',
    consultants_busy: 'Mashquul',
    consultants_sessions: 'fadhiyo',
    consultants_view_all: 'Arag Dhammaan →',
    ambassadors_label: 'Barnaamijka Safiirrada',
    ambassadors_heading_1: 'Kasb halka aad faafiseyso',
    ambassadors_heading_2: 'wanaagga bulshaddaada',
    ambassadors_subtext: 'La wadaag Rafeeq shabakaddaada oo kasb kireysiga soo noqnoqda ee ku saabsan dhammaan ruqsadda aad tixraacdo. Sida aad u kobceyso, heerkaagu wuu kordhi doonaa — iyo dakhligaaga.',
    ambassadors_commission: 'kireysiga',
    ambassadors_referrals: 'tixraacyo',
    ambassadors_cta_apply: 'Codso Hadda',
    ambassadors_cta_learn: 'Wax Dheeraad ah Baro',
    ambassadors_ready_heading: 'Ma diyaar u tahay inaad noqoto Safiir?',
    ambassadors_ready_subtext: 'Codso daqiiqado gudahood. Xad ugu yaraan ah oo raacayaasha looma baahna — kaliya jacayl run ah oo ku saabsan caawinta bulshaddaada inay ku kobcaan Rafeeq.',
    seniors_label: 'Waayeelka',
    seniors_heading_1: 'Weydii culimaa la xaqiijiyay.',
    seniors_heading_2: 'Hel jawaab shakhsi ah.',
    seniors_subtext: 'Hagaha Culimada wuxuu kula xidaa culimaa Islaamiyiin la xaqiijiyay oo si shakhsi ah uga jawaaba su\'aalahaaga — adoo leh dulqaadka, xurumada, iyo qoto-dheerida ay mudan yihiin waayeelka.',
    seniors_cta_tour: 'Qaado Safarka Isdhexgalka ah',
    seniors_cta_browse: 'Raadi Hagaha Culimada',
    seniors_featured_label: 'Culimaa Muuqda',
    seniors_featured_heading: 'La xiriir qaar ka mid ah culimadeena la xaqiijiyay',
    seniors_available: 'Diyaar',
    seniors_busy: 'Mashquul',
    seniors_answers: 'jawaabo',
    seniors_take_tour: 'Qaado Safarka',
    onboarding_label: 'Diiwaangelinta Ammaan ah',
    onboarding_heading: 'Bilow safarkaga maanta. Xogahaagu waxay kugula jirtaa.',
    onboarding_subtext: 'Samee akoonkaaga 2 daqiiqo gudahood. Waxaan uruurinaa kaliya waxa aan u baahanahay si aan u gaarka u dhigno khibradaada tababarka AI — waxaadna tirtiri kartaa wax walba, mar kasta.',
    onboarding_cta_create: 'Samee Akoon Bilaash ah',
    onboarding_cta_signin: 'Gal dashboard-ka',
    onboarding_privacy_note: '🔐 Galitaanka furaha WebAuthn waa la heli karaa · GDPR waafaqsan · Xoriyad aqoon-la\'aanta',
    footer_features: 'Astaamaha',
    footer_pricing: 'Qiimaha',
    footer_ambassadors: 'Safiirrada',
    footer_signin: 'Gal',
    lang_switcher_label: 'Luqadda',
    lang_reading_in: 'Akhrinta',
  },

  ur: {
    nav_features: 'خصوصیات',
    nav_pricing: 'قیمتیں',
    nav_all_plans: 'تمام منصوبے',
    nav_community: 'کمیونٹی',
    nav_ambassadors: 'سفیر',
    nav_consultants: 'مشیر',
    nav_sign_in: 'سائن ان',
    nav_get_started: 'شروع کریں',
    hero_badge: 'AI اسلامی ذاتی معاون اور ترقی کا نظام',
    hero_headline_1: 'آپ کا AI ساتھی',
    hero_headline_highlight: 'روزانہ ترقی',
    hero_headline_2: 'اسلامی حکمت میں جڑا ہوا',
    hero_subheadline: 'رفیق ایک برطانوی ٹیک کمپنی ہے جو دنیا بھر کے مسلمانوں کی خدمت کرنا چاہتی ہے — مثبت نفسیات، قرآنی اصولوں اور ریئل ٹائم AI کوچنگ کو ملا کر مسلم طلباء، گریجویٹس اور پیشہ ور افراد کو اپنی زبان میں مستقل اور بامقصد زندگی بنانے میں مدد کرتی ہے۔',
    hero_cta_primary: 'اپنا سفر شروع کریں',
    hero_cta_secondary: 'قیمتیں دیکھیں',
    hero_download_label: 'ایپ ڈاؤن لوڈ کریں:',
    hero_trust_encrypted: 'اینڈ ٹو اینڈ انکرپٹڈ',
    hero_trust_languages: '10 زبانیں سپورٹ',
    hero_trust_islamic: 'اسلامی فرسٹ ڈیزائن',
    hero_trust_voice: 'ریئل ٹائم وائس AI',
    hero_explore: 'دریافت کریں',
    features_label: 'بنیادی ستون',
    features_heading_1: 'آپ کے لیے مکمل طور پر بنایا گیا —',
    features_heading_2: 'ذہن، کیریئر اور روح',
    features_subtext: 'چھ مربوط ماڈیول جو مل کر ایک مسلم پیشہ ور کی زندگی کے ہر پہلو کو سپورٹ کرتے ہیں — روزمرہ کی عادات سے لے کر کیریئر کی کامیابیوں تک۔',
    pricing_label: 'شفاف قیمتیں',
    pricing_heading: 'ایک پلیٹ فارم، تین راستے',
    pricing_subtext: 'آپ کی زندگی کے مرحلے کے مطابق قیمتیں — آپ کے علاقے کی بنیاد پر خریداری کی طاقت کی برابری خودبخود لاگو ہوتی ہے۔ اپنی مقامی کرنسی میں موبائل منی سے ادائیگی کریں۔ کوئی چھپی ہوئی فیس نہیں، کسی بھی وقت منسوخ کریں۔',
    pricing_usd_note: 'USD حوالہ قیمت — نیچے اپنی مقامی کرنسی میں ادائیگی کریں',
    pricing_regional_label: 'موبائل منی سے ادائیگی کریں',
    pricing_regional_heading: 'مقامی ادائیگی کے لیے اپنا علاقہ منتخب کریں',
    pricing_regional_subtext: 'تمام ادائیگیاں قابل اعتماد مقامی موبائل منی فراہم کنندگان کے ذریعے پروسیس کی جاتی ہیں — کارڈ کی ضرورت نہیں۔',
    pricing_pay_here: 'یہاں ادائیگی کریں',
    pricing_no_card: '💳 کارڈ چیک آؤٹ دستیاب نہیں — تمام ادائیگیاں کم فیس اور بہتر رسائی کے لیے علاقائی موبائل منی کے ذریعے روٹ کی جاتی ہیں۔',
    pricing_ppp_note: '🌍 مقامی قیمتیں کینیا، یوگنڈا، صومالیہ، انڈونیشیا، پاکستان اور ملیشیا کے لیے دستیاب ہیں۔',
    pricing_then_choose: 'پھر نیچے اپنی علاقائی ادائیگی کا طریقہ منتخب کریں ↓',
    community_label: 'عالمی کمیونٹی',
    community_heading_1: 'مسلم ڈائسپورا کے لیے،',
    community_heading_2: 'جہاں بھی آپ ہوں',
    community_subtext: 'رفیق صومالی، عرب، جنوبی ایشیائی اور جنوب مشرقی ایشیائی کمیونٹیز کو ذہن میں رکھ کر بنایا گیا — تمام مکاتب فکر میں قبول شدہ عالمی اسلامی تصورات استعمال کرتے ہوئے۔ کوئی ثقافتی رکاوٹ نہیں، صرف حقیقی سپورٹ۔',
    community_cta: 'کمیونٹی میں شامل ہوں',
    community_stat_languages: 'زبانیں سپورٹ',
    community_stat_pillars: 'AI کوچنگ ستون',
    community_stat_growth: 'روزانہ ترقی کی صلاحیت',
    community_stat_privacy: 'پرائیویسی فرسٹ ڈیزائن',
    consultants_label: '1-آن-1 مشیر',
    consultants_heading_1: 'ایک سیشن بک کریں',
    consultants_heading_2: 'مسلم پیشہ ور کوچ کے ساتھ',
    consultants_subtext: 'ہمارے تصدیق شدہ فری لانس مشیر ذاتی 1-آن-1 ویڈیو سیشن پیش کرتے ہیں جو کیریئر، کاروبار، ذہنی صحت اور روحانی ترقی کا احاطہ کرتے ہیں — سب ایمان کے شعور کے نقطہ نظر سے۔',
    consultants_cta_browse: 'تمام مشیر دیکھیں',
    consultants_cta_become: 'مشیر بنیں',
    consultants_featured_label: 'نمایاں مشیر',
    consultants_featured_heading: 'ابھی بکنگ کے لیے دستیاب',
    consultants_available: 'دستیاب',
    consultants_busy: 'مصروف',
    consultants_sessions: 'سیشن',
    consultants_view_all: 'سب دیکھیں →',
    ambassadors_label: 'سفیر پروگرام',
    ambassadors_heading_1: 'کمائیں جبکہ آپ پھیلاتے ہیں',
    ambassadors_heading_2: 'اپنی کمیونٹی میں بھلائی',
    ambassadors_subtext: 'رفیق کو اپنے نیٹ ورک کے ساتھ شیئر کریں اور ہر اس سبسکرپشن پر بار بار کمیشن کمائیں جو آپ ریفر کرتے ہیں۔ جتنا آپ بڑھتے ہیں، آپ کا ٹیئر اتنا ہی اونچا ہوتا ہے — اور آپ کی کمائی بھی۔',
    ambassadors_commission: 'کمیشن',
    ambassadors_referrals: 'ریفرل',
    ambassadors_cta_apply: 'ابھی درخواست دیں',
    ambassadors_cta_learn: 'مزید جانیں',
    ambassadors_ready_heading: 'کیا آپ سفیر بننے کے لیے تیار ہیں؟',
    ambassadors_ready_subtext: 'منٹوں میں درخواست دیں۔ کوئی کم از کم فالوورز کی ضرورت نہیں — صرف اپنی کمیونٹی کو رفیق کے ساتھ بڑھانے میں مدد کرنے کا حقیقی جذبہ۔',
    seniors_label: 'بزرگوں کے لیے',
    seniors_heading_1: 'ایک تصدیق شدہ عالم سے پوچھیں۔',
    seniors_heading_2: 'ذاتی جواب پائیں۔',
    seniors_subtext: 'ہمارا علماء ڈائریکٹری آپ کو تصدیق شدہ اسلامی علماء سے جوڑتا ہے جو آپ کے سوالات کا ذاتی طور پر جواب دیتے ہیں — اس صبر، احترام اور گہرائی کے ساتھ جو بزرگ مستحق ہیں۔',
    seniors_cta_tour: 'انٹرایکٹو ٹور لیں',
    seniors_cta_browse: 'علماء ڈائریکٹری دیکھیں',
    seniors_featured_label: 'نمایاں علماء',
    seniors_featured_heading: 'ہمارے کچھ تصدیق شدہ علماء سے ملیں',
    seniors_available: 'دستیاب',
    seniors_busy: 'مصروف',
    seniors_answers: 'جوابات',
    seniors_take_tour: 'ٹور لیں',
    onboarding_label: 'محفوظ آن بورڈنگ',
    onboarding_heading: 'آج اپنا سفر شروع کریں۔ آپ کا ڈیٹا آپ کا رہتا ہے۔',
    onboarding_subtext: '2 منٹ سے کم میں اپنا اکاؤنٹ بنائیں۔ ہم صرف وہی جمع کرتے ہیں جو ہمیں آپ کے AI کوچنگ تجربے کو ذاتی بنانے کے لیے چاہیے — اور آپ کسی بھی وقت سب کچھ حذف کر سکتے ہیں۔',
    onboarding_cta_create: 'مفت اکاؤنٹ بنائیں',
    onboarding_cta_signin: 'ڈیش بورڈ میں سائن ان کریں',
    onboarding_privacy_note: '🔐 WebAuthn پاس کی لاگ ان دستیاب · GDPR کے مطابق · زیرو نالج پرائیویسی',
    footer_features: 'خصوصیات',
    footer_pricing: 'قیمتیں',
    footer_ambassadors: 'سفیر',
    footer_signin: 'سائن ان',
    lang_switcher_label: 'زبان',
    lang_reading_in: 'پڑھ رہے ہیں',
  },

  fr: {
    nav_features: 'Fonctionnalités',
    nav_pricing: 'Tarifs',
    nav_all_plans: 'Tous les plans',
    nav_community: 'Communauté',
    nav_ambassadors: 'Ambassadeurs',
    nav_consultants: 'Consultants',
    nav_sign_in: 'Se connecter',
    nav_get_started: 'Commencer',
    hero_badge: 'Assistant personnel islamique IA & Écosystème de croissance',
    hero_headline_1: 'Votre compagnon IA pour',
    hero_headline_highlight: 'la croissance quotidienne',
    hero_headline_2: 'ancrée dans la sagesse islamique',
    hero_subheadline: 'Rafeeq est une entreprise technologique britannique qui vise à servir les musulmans du monde entier — alliant psychologie positive, principes coraniques et coaching IA en temps réel pour aider les étudiants, diplômés et professionnels musulmans à construire des vies cohérentes et purposées dans leur propre langue.',
    hero_cta_primary: 'Commencez votre voyage',
    hero_cta_secondary: 'Voir les tarifs',
    hero_download_label: 'Télécharger l\'application :',
    hero_trust_encrypted: 'Chiffrement de bout en bout',
    hero_trust_languages: '10 langues supportées',
    hero_trust_islamic: 'Design islamique en premier',
    hero_trust_voice: 'IA vocale en temps réel',
    hero_explore: 'Explorer',
    features_label: 'Piliers fondamentaux',
    features_heading_1: 'Conçu pour vous en entier —',
    features_heading_2: 'esprit, carrière et âme',
    features_subtext: 'Six modules intégrés qui travaillent ensemble pour soutenir chaque dimension de la vie d\'un professionnel musulman — des habitudes quotidiennes aux percées de carrière.',
    pricing_label: 'Tarification transparente',
    pricing_heading: 'Une plateforme, trois voies',
    pricing_subtext: 'Tarifs adaptés à votre étape de vie — avec la parité de pouvoir d\'achat appliquée automatiquement selon votre région. Payez via mobile money dans votre devise locale. Pas de frais cachés, annulez à tout moment.',
    pricing_usd_note: 'Prix de référence USD — payez dans votre devise locale ci-dessous',
    pricing_regional_label: 'Payer avec Mobile Money',
    pricing_regional_heading: 'Sélectionnez votre région pour payer localement',
    pricing_regional_subtext: 'Tous les paiements sont traités par des fournisseurs locaux de mobile money de confiance — aucune carte requise.',
    pricing_pay_here: 'Payer ici',
    pricing_no_card: '💳 Le paiement par carte n\'est pas disponible — tous les paiements sont acheminés via le mobile money régional pour des frais réduits et une meilleure accessibilité.',
    pricing_ppp_note: '🌍 Tarification locale disponible pour le Kenya, l\'Ouganda, la Somalie, l\'Indonésie, le Pakistan et la Malaisie.',
    pricing_then_choose: 'Puis choisissez votre paiement régional ci-dessous ↓',
    community_label: 'Communauté mondiale',
    community_heading_1: 'Pour la diaspora musulmane,',
    community_heading_2: 'où que vous soyez',
    community_subtext: 'Rafeeq a été construit en pensant aux communautés somaliennes, arabes, sud-asiatiques et sud-est asiatiques — en utilisant des concepts islamiques universels acceptés dans toutes les écoles de pensée. Pas de barrières culturelles, juste un soutien authentique.',
    community_cta: 'Rejoindre la communauté',
    community_stat_languages: 'Langues supportées',
    community_stat_pillars: 'Piliers de coaching IA',
    community_stat_growth: 'Potentiel de croissance quotidien',
    community_stat_privacy: 'Design axé sur la confidentialité',
    consultants_label: 'Consultants 1-à-1',
    consultants_heading_1: 'Réservez une session avec un',
    consultants_heading_2: 'coach professionnel musulman',
    consultants_subtext: 'Nos consultants freelances vérifiés offrent des sessions vidéo personnalisées 1-à-1 couvrant la carrière, les affaires, le bien-être mental et la croissance spirituelle — tout d\'une perspective consciente de la foi.',
    consultants_cta_browse: 'Parcourir tous les consultants',
    consultants_cta_become: 'Devenir consultant',
    consultants_featured_label: 'Consultants en vedette',
    consultants_featured_heading: 'Disponibles pour réservation maintenant',
    consultants_available: 'Disponible',
    consultants_busy: 'Occupé',
    consultants_sessions: 'sessions',
    consultants_view_all: 'Voir tout →',
    ambassadors_label: 'Programme Ambassadeur',
    ambassadors_heading_1: 'Gagnez tout en répandant',
    ambassadors_heading_2: 'la bonté dans votre communauté',
    ambassadors_subtext: 'Partagez Rafeeq avec votre réseau et gagnez des commissions récurrentes sur chaque abonnement que vous référez. Plus vous grandissez, plus votre niveau est élevé — et vos gains aussi.',
    ambassadors_commission: 'commission',
    ambassadors_referrals: 'références',
    ambassadors_cta_apply: 'Postuler maintenant',
    ambassadors_cta_learn: 'En savoir plus',
    ambassadors_ready_heading: 'Prêt à devenir Ambassadeur ?',
    ambassadors_ready_subtext: 'Postulez en quelques minutes. Aucun minimum d\'abonnés requis — juste une passion authentique pour aider votre communauté à grandir avec Rafeeq.',
    seniors_label: 'Pour les Seniors',
    seniors_heading_1: 'Demandez à un érudit vérifié.',
    seniors_heading_2: 'Obtenez une réponse personnelle.',
    seniors_subtext: 'Notre Répertoire d\'Érudits vous connecte avec des érudits islamiques vérifiés qui répondent personnellement à vos questions — avec la patience, le respect et la profondeur que les seniors méritent.',
    seniors_cta_tour: 'Prendre la visite interactive',
    seniors_cta_browse: 'Parcourir le répertoire des érudits',
    seniors_featured_label: 'Érudits en vedette',
    seniors_featured_heading: 'Rencontrez quelques-uns de nos érudits vérifiés',
    seniors_available: 'Disponible',
    seniors_busy: 'Occupé',
    seniors_answers: 'réponses',
    seniors_take_tour: 'Prendre la visite',
    onboarding_label: 'Inscription sécurisée',
    onboarding_heading: 'Commencez votre voyage aujourd\'hui. Vos données restent les vôtres.',
    onboarding_subtext: 'Créez votre compte en moins de 2 minutes. Nous collectons uniquement ce dont nous avons besoin pour personnaliser votre expérience de coaching IA — et vous pouvez tout supprimer, à tout moment.',
    onboarding_cta_create: 'Créer un compte gratuit',
    onboarding_cta_signin: 'Se connecter au tableau de bord',
    onboarding_privacy_note: '🔐 Connexion par clé WebAuthn disponible · Conforme RGPD · Confidentialité zéro connaissance',
    footer_features: 'Fonctionnalités',
    footer_pricing: 'Tarifs',
    footer_ambassadors: 'Ambassadeurs',
    footer_signin: 'Se connecter',
    lang_switcher_label: 'Langue',
    lang_reading_in: 'Lecture en',
  },

  sw: {
    nav_features: 'Vipengele',
    nav_pricing: 'Bei',
    nav_all_plans: 'Mipango Yote',
    nav_community: 'Jamii',
    nav_ambassadors: 'Mabalozi',
    nav_consultants: 'Washauri',
    nav_sign_in: 'Ingia',
    nav_get_started: 'Anza',
    hero_badge: 'Msaidizi wa Kibinafsi wa Kiislamu wa AI & Mfumo wa Ukuaji',
    hero_headline_1: 'Mwenzako wa AI kwa',
    hero_headline_highlight: 'ukuaji wa kila siku',
    hero_headline_2: 'uliozikwa katika hekima ya Kiislamu',
    hero_subheadline: 'Rafeeq ni kampuni ya teknolojia ya Uingereza inayolenga kuhudumia Waislamu duniani kote popote walipo — ikichanganya saikolojia chanya, kanuni za Qur\'an, na mafunzo ya AI ya wakati halisi kusaidia wanafunzi, wahitimu, na wataalamu wa Kiislamu kujenga maisha thabiti na yenye kusudi katika lugha yao.',
    hero_cta_primary: 'Anza Safari Yako',
    hero_cta_secondary: 'Angalia Bei',
    hero_download_label: 'Pakua programu:',
    hero_trust_encrypted: 'Usimbaji fiche mwisho hadi mwisho',
    hero_trust_languages: 'Lugha 10 zinazotumika',
    hero_trust_islamic: 'Muundo wa Kiislamu kwanza',
    hero_trust_voice: 'AI ya sauti ya wakati halisi',
    hero_explore: 'Chunguza',
    features_label: 'Nguzo Kuu',
    features_heading_1: 'Imejengwa kwa ajili yako yote —',
    features_heading_2: 'akili, kazi, na roho',
    features_subtext: 'Moduli sita zilizounganishwa zinazofanya kazi pamoja kusaidia kila kipengele cha maisha ya mtaalamu wa Kiislamu — kutoka tabia za kila siku hadi mafanikio ya kazi.',
    pricing_label: 'Bei Wazi',
    pricing_heading: 'Jukwaa moja, njia tatu',
    pricing_subtext: 'Bei zinazofaa hatua yako ya maisha — na usawa wa nguvu ya ununuzi ukitumika kiotomatiki kulingana na eneo lako. Lipa kupitia pesa za simu katika sarafu yako ya ndani. Hakuna ada zilizofichwa, ghairi wakati wowote.',
    pricing_usd_note: 'Bei ya kumbukumbu ya USD — lipa kwa sarafu yako ya ndani hapa chini',
    pricing_regional_label: 'Lipa kwa Pesa za Simu',
    pricing_regional_heading: 'Chagua eneo lako kulipa ndani',
    pricing_regional_subtext: 'Malipo yote yanashughulikiwa kupitia watoa huduma wa pesa za simu wa ndani wanaotegemewa — hakuna kadi inayohitajika.',
    pricing_pay_here: 'Lipa hapa',
    pricing_no_card: '💳 Malipo ya kadi hayapatikani — malipo yote yanaelekezwa kupitia pesa za simu za kikanda kwa ada ndogo na upatikanaji bora.',
    pricing_ppp_note: '🌍 Bei za ndani zinapatikana kwa Kenya, Uganda, Somalia, Indonesia, Pakistan, na Malaysia.',
    pricing_then_choose: 'Kisha chagua malipo yako ya kikanda hapa chini ↓',
    community_label: 'Jamii ya Kimataifa',
    community_heading_1: 'Kwa diaspora ya Kiislamu,',
    community_heading_2: 'popote ulipo',
    community_subtext: 'Rafeeq ilijengwa kwa kuzingatia jamii za Kisomali, Kiarabu, Asia Kusini, na Asia ya Kusini-Mashariki — kwa kutumia dhana za Kiislamu za ulimwengu zinazokubaliwa katika shule zote za mawazo. Hakuna vizuizi vya kitamaduni, msaada wa kweli tu.',
    community_cta: 'Jiunge na jamii',
    community_stat_languages: 'Lugha zinazotumika',
    community_stat_pillars: 'Nguzo za mafunzo ya AI',
    community_stat_growth: 'Uwezo wa ukuaji wa kila siku',
    community_stat_privacy: 'Muundo wa faragha kwanza',
    consultants_label: 'Washauri 1-kwa-1',
    consultants_heading_1: 'Hifadhi kikao na',
    consultants_heading_2: 'kocha mtaalamu wa Kiislamu',
    consultants_subtext: 'Washauri wetu wa kujitegemea waliothibitishwa hutoa vikao vya video vya kibinafsi vya 1-kwa-1 vinavyoshughulikia kazi, biashara, afya ya akili, na ukuaji wa kiroho — vyote kutoka mtazamo wa imani.',
    consultants_cta_browse: 'Vinjari Washauri Wote',
    consultants_cta_become: 'Kuwa Mshauri',
    consultants_featured_label: 'Washauri Maarufu',
    consultants_featured_heading: 'Wanapatikana kwa uhifadhi sasa',
    consultants_available: 'Anapatikana',
    consultants_busy: 'Ameshughulika',
    consultants_sessions: 'vikao',
    consultants_view_all: 'Angalia Yote →',
    ambassadors_label: 'Programu ya Mabalozi',
    ambassadors_heading_1: 'Pata kipato ukieneza',
    ambassadors_heading_2: 'wema katika jamii yako',
    ambassadors_subtext: 'Shiriki Rafeeq na mtandao wako na upate kamisheni zinazoendelea kwa kila usajili unaorejelea. Kadri unavyokua, ndivyo kiwango chako kinavyoongezeka — na mapato yako pia.',
    ambassadors_commission: 'kamisheni',
    ambassadors_referrals: 'marejeo',
    ambassadors_cta_apply: 'Omba Sasa',
    ambassadors_cta_learn: 'Jifunze Zaidi',
    ambassadors_ready_heading: 'Uko tayari kuwa Balozi?',
    ambassadors_ready_subtext: 'Omba kwa dakika chache. Hakuna kiwango cha chini cha wafuatiliaji kinachohitajika — shauku ya kweli tu ya kusaidia jamii yako kukua na Rafeeq.',
    seniors_label: 'Kwa Wazee',
    seniors_heading_1: 'Uliza mwanachuoni aliyethibitishwa.',
    seniors_heading_2: 'Pata jibu la kibinafsi.',
    seniors_subtext: 'Saraka yetu ya Wanachuoni inakuunganisha na wanachuoni wa Kiislamu waliothibitishwa ambao wanajibu maswali yako kibinafsi — kwa uvumilivu, heshima, na kina ambacho wazee wanastahili.',
    seniors_cta_tour: 'Chukua Ziara ya Mwingiliano',
    seniors_cta_browse: 'Vinjari Saraka ya Wanachuoni',
    seniors_featured_label: 'Wanachuoni Maarufu',
    seniors_featured_heading: 'Kutana na baadhi ya wanachuoni wetu waliothibitishwa',
    seniors_available: 'Anapatikana',
    seniors_busy: 'Ameshughulika',
    seniors_answers: 'majibu',
    seniors_take_tour: 'Chukua Ziara',
    onboarding_label: 'Usajili Salama',
    onboarding_heading: 'Anza safari yako leo. Data yako inabaki yako.',
    onboarding_subtext: 'Unda akaunti yako kwa chini ya dakika 2. Tunakusanya tu tunachohitaji kubinafsisha uzoefu wako wa mafunzo ya AI — na unaweza kufuta kila kitu, wakati wowote.',
    onboarding_cta_create: 'Unda Akaunti ya Bure',
    onboarding_cta_signin: 'Ingia kwenye dashibodi',
    onboarding_privacy_note: '🔐 Kuingia kwa ufunguo wa WebAuthn kunapatikana · Inafuata GDPR · Faragha ya maarifa sifuri',
    footer_features: 'Vipengele',
    footer_pricing: 'Bei',
    footer_ambassadors: 'Mabalozi',
    footer_signin: 'Ingia',
    lang_switcher_label: 'Lugha',
    lang_reading_in: 'Unasoma kwa',
  },

  id: {
    nav_features: 'Fitur',
    nav_pricing: 'Harga',
    nav_all_plans: 'Semua Paket',
    nav_community: 'Komunitas',
    nav_ambassadors: 'Duta',
    nav_consultants: 'Konsultan',
    nav_sign_in: 'Masuk',
    nav_get_started: 'Mulai',
    hero_badge: 'Asisten Pribadi Islam AI & Ekosistem Pertumbuhan',
    hero_headline_1: 'Teman AI Anda untuk',
    hero_headline_highlight: 'pertumbuhan harian',
    hero_headline_2: 'yang berakar pada hikmah Islam',
    hero_subheadline: 'Rafeeq adalah perusahaan teknologi berbasis UK yang bertujuan melayani Muslim di seluruh dunia di mana pun mereka berada — memadukan psikologi positif, prinsip-prinsip Quran, dan pelatihan AI real-time untuk membantu pelajar, lulusan, dan profesional Muslim membangun kehidupan yang konsisten dan bertujuan dalam bahasa mereka sendiri.',
    hero_cta_primary: 'Mulai Perjalanan Anda',
    hero_cta_secondary: 'Lihat Harga',
    hero_download_label: 'Unduh aplikasi:',
    hero_trust_encrypted: 'Enkripsi ujung ke ujung',
    hero_trust_languages: '10 bahasa didukung',
    hero_trust_islamic: 'Desain Islam-first',
    hero_trust_voice: 'AI suara real-time',
    hero_explore: 'Jelajahi',
    features_label: 'Pilar Utama',
    features_heading_1: 'Dibangun untuk Anda seutuhnya —',
    features_heading_2: 'pikiran, karier, dan jiwa',
    features_subtext: 'Enam modul terintegrasi yang bekerja bersama untuk mendukung setiap dimensi kehidupan profesional Muslim — dari kebiasaan harian hingga terobosan karier.',
    pricing_label: 'Harga Transparan',
    pricing_heading: 'Satu platform, tiga jalur',
    pricing_subtext: 'Harga sesuai tahap hidup Anda — dengan paritas daya beli diterapkan secara otomatis berdasarkan wilayah Anda. Bayar melalui mobile money dalam mata uang lokal Anda. Tidak ada biaya tersembunyi, batalkan kapan saja.',
    pricing_usd_note: 'Harga referensi USD — bayar dalam mata uang lokal Anda di bawah',
    pricing_regional_label: 'Bayar dengan Mobile Money',
    pricing_regional_heading: 'Pilih wilayah Anda untuk bayar lokal',
    pricing_regional_subtext: 'Semua pembayaran diproses melalui penyedia mobile money lokal terpercaya — tidak perlu kartu.',
    pricing_pay_here: 'Bayar di sini',
    pricing_no_card: '💳 Checkout kartu tidak tersedia — semua pembayaran diarahkan melalui mobile money regional untuk biaya lebih rendah dan aksesibilitas lebih baik.',
    pricing_ppp_note: '🌍 Harga lokal tersedia untuk Kenya, Uganda, Somalia, Indonesia, Pakistan, dan Malaysia.',
    pricing_then_choose: 'Kemudian pilih pembayaran regional Anda di bawah ↓',
    community_label: 'Komunitas Global',
    community_heading_1: 'Untuk diaspora Muslim,',
    community_heading_2: 'di mana pun Anda berada',
    community_subtext: 'Rafeeq dibangun dengan mempertimbangkan komunitas Somalia, Arab, Asia Selatan, dan Asia Tenggara — menggunakan konsep Islam universal yang diterima di semua mazhab. Tidak ada hambatan budaya, hanya dukungan tulus.',
    community_cta: 'Bergabung dengan komunitas',
    community_stat_languages: 'Bahasa didukung',
    community_stat_pillars: 'Pilar pelatihan AI',
    community_stat_growth: 'Potensi pertumbuhan harian',
    community_stat_privacy: 'Desain privasi-first',
    consultants_label: 'Konsultan 1-on-1',
    consultants_heading_1: 'Pesan sesi dengan',
    consultants_heading_2: 'pelatih profesional Muslim',
    consultants_subtext: 'Konsultan freelance kami yang terverifikasi menawarkan sesi video 1-on-1 yang dipersonalisasi mencakup karier, bisnis, kesehatan mental, dan pertumbuhan spiritual — semua dari perspektif yang sadar iman.',
    consultants_cta_browse: 'Jelajahi Semua Konsultan',
    consultants_cta_become: 'Jadi Konsultan',
    consultants_featured_label: 'Konsultan Unggulan',
    consultants_featured_heading: 'Tersedia untuk pemesanan sekarang',
    consultants_available: 'Tersedia',
    consultants_busy: 'Sibuk',
    consultants_sessions: 'sesi',
    consultants_view_all: 'Lihat Semua →',
    ambassadors_label: 'Program Duta',
    ambassadors_heading_1: 'Hasilkan sambil menyebarkan',
    ambassadors_heading_2: 'kebaikan di komunitas Anda',
    ambassadors_subtext: 'Bagikan Rafeeq dengan jaringan Anda dan dapatkan komisi berulang untuk setiap langganan yang Anda referensikan. Semakin Anda berkembang, semakin tinggi tingkat Anda — dan penghasilan Anda.',
    ambassadors_commission: 'komisi',
    ambassadors_referrals: 'referral',
    ambassadors_cta_apply: 'Daftar Sekarang',
    ambassadors_cta_learn: 'Pelajari Lebih Lanjut',
    ambassadors_ready_heading: 'Siap menjadi Duta?',
    ambassadors_ready_subtext: 'Daftar dalam hitungan menit. Tidak ada minimum pengikut yang diperlukan — hanya semangat tulus untuk membantu komunitas Anda berkembang dengan Rafeeq.',
    seniors_label: 'Untuk Lansia',
    seniors_heading_1: 'Tanya ulama terverifikasi.',
    seniors_heading_2: 'Dapatkan jawaban pribadi.',
    seniors_subtext: 'Direktori Ulama kami menghubungkan Anda dengan ulama Islam terverifikasi yang merespons pertanyaan Anda secara pribadi — dengan kesabaran, rasa hormat, dan kedalaman yang layak diterima lansia.',
    seniors_cta_tour: 'Ikuti Tur Interaktif',
    seniors_cta_browse: 'Jelajahi Direktori Ulama',
    seniors_featured_label: 'Ulama Unggulan',
    seniors_featured_heading: 'Temui beberapa ulama terverifikasi kami',
    seniors_available: 'Tersedia',
    seniors_busy: 'Sibuk',
    seniors_answers: 'jawaban',
    seniors_take_tour: 'Ikuti Tur',
    onboarding_label: 'Pendaftaran Aman',
    onboarding_heading: 'Mulai perjalanan Anda hari ini. Data Anda tetap milik Anda.',
    onboarding_subtext: 'Buat akun Anda dalam waktu kurang dari 2 menit. Kami hanya mengumpulkan apa yang kami butuhkan untuk mempersonalisasi pengalaman pelatihan AI Anda — dan Anda dapat menghapus semuanya, kapan saja.',
    onboarding_cta_create: 'Buat Akun Gratis',
    onboarding_cta_signin: 'Masuk ke dasbor',
    onboarding_privacy_note: '🔐 Login kunci WebAuthn tersedia · Sesuai GDPR · Privasi zero-knowledge',
    footer_features: 'Fitur',
    footer_pricing: 'Harga',
    footer_ambassadors: 'Duta',
    footer_signin: 'Masuk',
    lang_switcher_label: 'Bahasa',
    lang_reading_in: 'Membaca dalam',
  },

  ms: {
    nav_features: 'Ciri-ciri',
    nav_pricing: 'Harga',
    nav_all_plans: 'Semua Pelan',
    nav_community: 'Komuniti',
    nav_ambassadors: 'Duta',
    nav_consultants: 'Perunding',
    nav_sign_in: 'Log Masuk',
    nav_get_started: 'Mulakan',
    hero_badge: 'Pembantu Peribadi Islam AI & Ekosistem Pertumbuhan',
    hero_headline_1: 'Rakan AI anda untuk',
    hero_headline_highlight: 'pertumbuhan harian',
    hero_headline_2: 'berakar pada hikmah Islam',
    hero_subheadline: 'Rafeeq ialah syarikat teknologi berasaskan UK yang bertujuan melayani Muslim di seluruh dunia di mana sahaja mereka berada — menggabungkan psikologi positif, prinsip-prinsip Al-Quran, dan latihan AI masa nyata untuk membantu pelajar, graduan, dan profesional Muslim membina kehidupan yang konsisten dan bertujuan dalam bahasa mereka sendiri.',
    hero_cta_primary: 'Mulakan Perjalanan Anda',
    hero_cta_secondary: 'Lihat Harga',
    hero_download_label: 'Muat turun apl:',
    hero_trust_encrypted: 'Penyulitan hujung ke hujung',
    hero_trust_languages: '10 bahasa disokong',
    hero_trust_islamic: 'Reka bentuk Islam-first',
    hero_trust_voice: 'AI suara masa nyata',
    hero_explore: 'Terokai',
    features_label: 'Tiang Utama',
    features_heading_1: 'Dibina untuk anda sepenuhnya —',
    features_heading_2: 'minda, kerjaya, dan jiwa',
    features_subtext: 'Enam modul bersepadu yang bekerja bersama untuk menyokong setiap dimensi kehidupan profesional Muslim — dari tabiat harian hingga kejayaan kerjaya.',
    pricing_label: 'Harga Telus',
    pricing_heading: 'Satu platform, tiga laluan',
    pricing_subtext: 'Harga sesuai dengan peringkat hidup anda — dengan pariti kuasa beli diterapkan secara automatik berdasarkan wilayah anda. Bayar melalui wang mudah alih dalam mata wang tempatan anda. Tiada yuran tersembunyi, batalkan bila-bila masa.',
    pricing_usd_note: 'Harga rujukan USD — bayar dalam mata wang tempatan anda di bawah',
    pricing_regional_label: 'Bayar dengan Wang Mudah Alih',
    pricing_regional_heading: 'Pilih wilayah anda untuk bayar secara tempatan',
    pricing_regional_subtext: 'Semua pembayaran diproses melalui penyedia wang mudah alih tempatan yang dipercayai — tiada kad diperlukan.',
    pricing_pay_here: 'Bayar di sini',
    pricing_no_card: '💳 Pembayaran kad tidak tersedia — semua pembayaran dihalakan melalui wang mudah alih serantau untuk yuran lebih rendah dan kebolehcapaian lebih baik.',
    pricing_ppp_note: '🌍 Harga tempatan tersedia untuk Kenya, Uganda, Somalia, Indonesia, Pakistan, dan Malaysia.',
    pricing_then_choose: 'Kemudian pilih pembayaran serantau anda di bawah ↓',
    community_label: 'Komuniti Global',
    community_heading_1: 'Untuk diaspora Muslim,',
    community_heading_2: 'di mana sahaja anda berada',
    community_subtext: 'Rafeeq dibina dengan mengambil kira komuniti Somalia, Arab, Asia Selatan, dan Asia Tenggara — menggunakan konsep Islam universal yang diterima di semua mazhab. Tiada halangan budaya, hanya sokongan tulen.',
    community_cta: 'Sertai komuniti',
    community_stat_languages: 'Bahasa disokong',
    community_stat_pillars: 'Tiang latihan AI',
    community_stat_growth: 'Potensi pertumbuhan harian',
    community_stat_privacy: 'Reka bentuk privasi-first',
    consultants_label: 'Perunding 1-on-1',
    consultants_heading_1: 'Tempah sesi dengan',
    consultants_heading_2: 'jurulatih profesional Muslim',
    consultants_subtext: 'Perunding bebas kami yang disahkan menawarkan sesi video 1-on-1 yang diperibadikan merangkumi kerjaya, perniagaan, kesihatan mental, dan pertumbuhan rohani — semua dari perspektif yang sedar iman.',
    consultants_cta_browse: 'Semak Imbas Semua Perunding',
    consultants_cta_become: 'Jadi Perunding',
    consultants_featured_label: 'Perunding Pilihan',
    consultants_featured_heading: 'Tersedia untuk tempahan sekarang',
    consultants_available: 'Tersedia',
    consultants_busy: 'Sibuk',
    consultants_sessions: 'sesi',
    consultants_view_all: 'Lihat Semua →',
    ambassadors_label: 'Program Duta',
    ambassadors_heading_1: 'Jana pendapatan sambil menyebarkan',
    ambassadors_heading_2: 'kebaikan dalam komuniti anda',
    ambassadors_subtext: 'Kongsi Rafeeq dengan rangkaian anda dan dapatkan komisen berulang untuk setiap langganan yang anda rujuk. Semakin anda berkembang, semakin tinggi tahap anda — dan pendapatan anda.',
    ambassadors_commission: 'komisen',
    ambassadors_referrals: 'rujukan',
    ambassadors_cta_apply: 'Mohon Sekarang',
    ambassadors_cta_learn: 'Ketahui Lebih Lanjut',
    ambassadors_ready_heading: 'Bersedia untuk menjadi Duta?',
    ambassadors_ready_subtext: 'Mohon dalam beberapa minit. Tiada minimum pengikut diperlukan — hanya semangat tulen untuk membantu komuniti anda berkembang dengan Rafeeq.',
    seniors_label: 'Untuk Warga Emas',
    seniors_heading_1: 'Tanya ulama yang disahkan.',
    seniors_heading_2: 'Dapatkan jawapan peribadi.',
    seniors_subtext: 'Direktori Ulama kami menghubungkan anda dengan ulama Islam yang disahkan yang menjawab soalan anda secara peribadi — dengan kesabaran, rasa hormat, dan kedalaman yang layak diterima warga emas.',
    seniors_cta_tour: 'Ikuti Lawatan Interaktif',
    seniors_cta_browse: 'Semak Imbas Direktori Ulama',
    seniors_featured_label: 'Ulama Pilihan',
    seniors_featured_heading: 'Temui beberapa ulama kami yang disahkan',
    seniors_available: 'Tersedia',
    seniors_busy: 'Sibuk',
    seniors_answers: 'jawapan',
    seniors_take_tour: 'Ikuti Lawatan',
    onboarding_label: 'Pendaftaran Selamat',
    onboarding_heading: 'Mulakan perjalanan anda hari ini. Data anda kekal milik anda.',
    onboarding_subtext: 'Buat akaun anda dalam masa kurang dari 2 minit. Kami hanya mengumpul apa yang kami perlukan untuk memperibadikan pengalaman latihan AI anda — dan anda boleh memadam segalanya, bila-bila masa.',
    onboarding_cta_create: 'Buat Akaun Percuma',
    onboarding_cta_signin: 'Log masuk ke papan pemuka',
    onboarding_privacy_note: '🔐 Log masuk kunci WebAuthn tersedia · Mematuhi GDPR · Privasi zero-knowledge',
    footer_features: 'Ciri-ciri',
    footer_pricing: 'Harga',
    footer_ambassadors: 'Duta',
    footer_signin: 'Log Masuk',
    lang_switcher_label: 'Bahasa',
    lang_reading_in: 'Membaca dalam',
  },

  tr: {
    nav_features: 'Özellikler',
    nav_pricing: 'Fiyatlar',
    nav_all_plans: 'Tüm Planlar',
    nav_community: 'Topluluk',
    nav_ambassadors: 'Elçiler',
    nav_consultants: 'Danışmanlar',
    nav_sign_in: 'Giriş Yap',
    nav_get_started: 'Başla',
    hero_badge: 'AI İslami Kişisel Asistan & Büyüme Ekosistemi',
    hero_headline_1: 'AI arkadaşınız',
    hero_headline_highlight: 'günlük büyüme',
    hero_headline_2: 'için İslami bilgeliğe dayalı',
    hero_subheadline: 'Rafeeq, dünyanın her yerindeki Müslümanlara hizmet etmeyi amaçlayan İngiltere merkezli bir teknoloji şirketidir — pozitif psikoloji, Kuran prensipleri ve gerçek zamanlı AI koçluğunu bir araya getirerek Müslüman öğrencilerin, mezunların ve profesyonellerin kendi dillerinde tutarlı ve amaçlı yaşamlar inşa etmelerine yardımcı olur.',
    hero_cta_primary: 'Yolculuğunuza Başlayın',
    hero_cta_secondary: 'Fiyatları Görüntüle',
    hero_download_label: 'Uygulamayı indir:',
    hero_trust_encrypted: 'Uçtan uca şifreli',
    hero_trust_languages: '10 dil destekleniyor',
    hero_trust_islamic: 'İslami öncelikli tasarım',
    hero_trust_voice: 'Gerçek zamanlı sesli AI',
    hero_explore: 'Keşfet',
    features_label: 'Temel Sütunlar',
    features_heading_1: 'Tüm benliğiniz için inşa edildi —',
    features_heading_2: 'zihin, kariyer ve ruh',
    features_subtext: 'Bir Müslüman profesyonelin hayatının her boyutunu desteklemek için birlikte çalışan altı entegre modül — günlük alışkanlıklardan kariyer atılımlarına kadar.',
    pricing_label: 'Şeffaf Fiyatlandırma',
    pricing_heading: 'Bir platform, üç yol',
    pricing_subtext: 'Hayatınızdaki konuma göre fiyatlandırılmış — bölgenize göre otomatik olarak uygulanan satın alma gücü paritesiyle. Yerel para biriminizde mobil para ile ödeyin. Gizli ücret yok, istediğiniz zaman iptal edin.',
    pricing_usd_note: 'USD referans fiyatı — aşağıda yerel para biriminizle ödeyin',
    pricing_regional_label: 'Mobil Para ile Öde',
    pricing_regional_heading: 'Yerel ödeme için bölgenizi seçin',
    pricing_regional_subtext: 'Tüm ödemeler güvenilir yerel mobil para sağlayıcıları aracılığıyla işlenir — kart gerekmez.',
    pricing_pay_here: 'Buradan öde',
    pricing_no_card: '💳 Kart ile ödeme mevcut değil — tüm ödemeler daha düşük ücretler ve daha iyi erişilebilirlik için bölgesel mobil para üzerinden yönlendirilir.',
    pricing_ppp_note: '🌍 Kenya, Uganda, Somali, Endonezya, Pakistan ve Malezya için yerel fiyatlandırma mevcuttur.',
    pricing_then_choose: 'Ardından aşağıdan bölgesel ödeme yönteminizi seçin ↓',
    community_label: 'Küresel Topluluk',
    community_heading_1: 'Müslüman diasporası için,',
    community_heading_2: 'nerede olursanız olun',
    community_subtext: 'Rafeeq, Somalili, Arap, Güney Asyalı ve Güneydoğu Asyalı topluluklar göz önünde bulundurularak inşa edildi — tüm düşünce okullarında kabul gören evrensel İslami kavramlar kullanılarak. Kültürel engel yok, sadece gerçek destek.',
    community_cta: 'Topluluğa katıl',
    community_stat_languages: 'Desteklenen dil',
    community_stat_pillars: 'AI koçluk sütunu',
    community_stat_growth: 'Günlük büyüme potansiyeli',
    community_stat_privacy: 'Gizlilik öncelikli tasarım',
    consultants_label: '1-e-1 Danışmanlar',
    consultants_heading_1: 'Bir seans rezervasyonu yapın',
    consultants_heading_2: 'Müslüman profesyonel koç ile',
    consultants_subtext: 'Onaylı serbest danışmanlarımız kariyer, iş, zihinsel sağlık ve ruhsal gelişimi kapsayan kişiselleştirilmiş 1-e-1 video seansları sunar — hepsi iman bilincine sahip bir perspektiften.',
    consultants_cta_browse: 'Tüm Danışmanlara Göz At',
    consultants_cta_become: 'Danışman Ol',
    consultants_featured_label: 'Öne Çıkan Danışmanlar',
    consultants_featured_heading: 'Şu anda rezervasyon için müsait',
    consultants_available: 'Müsait',
    consultants_busy: 'Meşgul',
    consultants_sessions: 'seans',
    consultants_view_all: 'Tümünü Gör →',
    ambassadors_label: 'Elçi Programı',
    ambassadors_heading_1: 'Yayarken kazanın',
    ambassadors_heading_2: 'topluluğunuzda iyiliği',
    ambassadors_subtext: 'Rafeeq\'i ağınızla paylaşın ve yönlendirdiğiniz her abonelik için tekrarlayan komisyon kazanın. Ne kadar büyürseniz, seviyeniz o kadar yükselir — ve kazancınız da.',
    ambassadors_commission: 'komisyon',
    ambassadors_referrals: 'yönlendirme',
    ambassadors_cta_apply: 'Şimdi Başvur',
    ambassadors_cta_learn: 'Daha Fazla Öğren',
    ambassadors_ready_heading: 'Elçi olmaya hazır mısınız?',
    ambassadors_ready_subtext: 'Dakikalar içinde başvurun. Minimum takipçi sayısı gerekmez — sadece topluluğunuzun Rafeeq ile büyümesine yardımcı olma konusunda gerçek bir tutku.',
    seniors_label: 'Yaşlılar İçin',
    seniors_heading_1: 'Onaylı bir alime sorun.',
    seniors_heading_2: 'Kişisel bir cevap alın.',
    seniors_subtext: 'Alimler Rehberimiz sizi sorularınıza kişisel olarak yanıt veren onaylı İslam alimleriyle buluşturur — yaşlıların hak ettiği sabır, saygı ve derinlikle.',
    seniors_cta_tour: 'Etkileşimli Turu Alın',
    seniors_cta_browse: 'Alimler Rehberini İncele',
    seniors_featured_label: 'Öne Çıkan Alimler',
    seniors_featured_heading: 'Onaylı alimlerimizden bazılarıyla tanışın',
    seniors_available: 'Müsait',
    seniors_busy: 'Meşgul',
    seniors_answers: 'cevap',
    seniors_take_tour: 'Turu Al',
    onboarding_label: 'Güvenli Kayıt',
    onboarding_heading: 'Yolculuğunuza bugün başlayın. Verileriniz size ait kalır.',
    onboarding_subtext: '2 dakikadan kısa sürede hesabınızı oluşturun. Yalnızca AI koçluk deneyiminizi kişiselleştirmek için ihtiyacımız olanı topluyoruz — ve her şeyi istediğiniz zaman silebilirsiniz.',
    onboarding_cta_create: 'Ücretsiz Hesap Oluştur',
    onboarding_cta_signin: 'Panele giriş yap',
    onboarding_privacy_note: '🔐 WebAuthn anahtar girişi mevcut · GDPR uyumlu · Sıfır bilgi gizliliği',
    footer_features: 'Özellikler',
    footer_pricing: 'Fiyatlar',
    footer_ambassadors: 'Elçiler',
    footer_signin: 'Giriş Yap',
    lang_switcher_label: 'Dil',
    lang_reading_in: 'Okuma dili',
  },

  hi: {
    nav_features: 'विशेषताएं',
    nav_pricing: 'मूल्य',
    nav_all_plans: 'सभी योजनाएं',
    nav_community: 'समुदाय',
    nav_ambassadors: 'राजदूत',
    nav_consultants: 'सलाहकार',
    nav_sign_in: 'साइन इन',
    nav_get_started: 'शुरू करें',
    hero_badge: 'AI इस्लामी व्यक्तिगत सहायक और विकास पारिस्थितिकी तंत्र',
    hero_headline_1: 'आपका AI साथी',
    hero_headline_highlight: 'दैनिक विकास',
    hero_headline_2: 'के लिए इस्लामी ज्ञान में निहित',
    hero_subheadline: 'रफीक एक UK आधारित टेक कंपनी है जो दुनिया भर के मुसलमानों की सेवा करना चाहती है — सकारात्मक मनोविज्ञान, कुरानी सिद्धांतों और रियल-टाइम AI कोचिंग को मिलाकर मुस्लिम छात्रों, स्नातकों और पेशेवरों को अपनी भाषा में सुसंगत, उद्देश्यपूर्ण जीवन बनाने में मदद करती है।',
    hero_cta_primary: 'अपनी यात्रा शुरू करें',
    hero_cta_secondary: 'मूल्य देखें',
    hero_download_label: 'ऐप डाउनलोड करें:',
    hero_trust_encrypted: 'एंड-टू-एंड एन्क्रिप्टेड',
    hero_trust_languages: '10 भाषाएं समर्थित',
    hero_trust_islamic: 'इस्लामी-फर्स्ट डिज़ाइन',
    hero_trust_voice: 'रियल-टाइम वॉयस AI',
    hero_explore: 'अन्वेषण करें',
    features_label: 'मुख्य स्तंभ',
    features_heading_1: 'आपके लिए पूरी तरह बनाया गया —',
    features_heading_2: 'मन, करियर और आत्मा',
    features_subtext: 'छह एकीकृत मॉड्यूल जो एक मुस्लिम पेशेवर के जीवन के हर आयाम को समर्थन देने के लिए मिलकर काम करते हैं — दैनिक आदतों से करियर की सफलताओं तक।',
    pricing_label: 'पारदर्शी मूल्य निर्धारण',
    pricing_heading: 'एक प्लेटफॉर्म, तीन रास्ते',
    pricing_subtext: 'आपके जीवन के चरण के अनुसार मूल्य निर्धारित — आपके क्षेत्र के आधार पर क्रय शक्ति समता स्वचालित रूप से लागू होती है। अपनी स्थानीय मुद्रा में मोबाइल मनी के माध्यम से भुगतान करें। कोई छिपी हुई फीस नहीं, कभी भी रद्द करें।',
    pricing_usd_note: 'USD संदर्भ मूल्य — नीचे अपनी स्थानीय मुद्रा में भुगतान करें',
    pricing_regional_label: 'मोबाइल मनी से भुगतान करें',
    pricing_regional_heading: 'स्थानीय भुगतान के लिए अपना क्षेत्र चुनें',
    pricing_regional_subtext: 'सभी भुगतान विश्वसनीय स्थानीय मोबाइल मनी प्रदाताओं के माध्यम से संसाधित किए जाते हैं — कोई कार्ड आवश्यक नहीं।',
    pricing_pay_here: 'यहां भुगतान करें',
    pricing_no_card: '💳 कार्ड चेकआउट उपलब्ध नहीं है — सभी भुगतान कम शुल्क और बेहतर पहुंच के लिए क्षेत्रीय मोबाइल मनी के माध्यम से रूट किए जाते हैं।',
    pricing_ppp_note: '🌍 केन्या, युगांडा, सोमालिया, इंडोनेशिया, पाकिस्तान और मलेशिया के लिए स्थानीय मूल्य निर्धारण उपलब्ध है।',
    pricing_then_choose: 'फिर नीचे अपना क्षेत्रीय भुगतान चुनें ↓',
    community_label: 'वैश्विक समुदाय',
    community_heading_1: 'मुस्लिम डायस्पोरा के लिए,',
    community_heading_2: 'जहां भी आप हों',
    community_subtext: 'रफीक सोमाली, अरब, दक्षिण एशियाई और दक्षिण-पूर्व एशियाई समुदायों को ध्यान में रखकर बनाया गया था — सभी विचार धाराओं में स्वीकृत सार्वभौमिक इस्लामी अवधारणाओं का उपयोग करते हुए। कोई सांस्कृतिक बाधा नहीं, केवल वास्तविक समर्थन।',
    community_cta: 'समुदाय में शामिल हों',
    community_stat_languages: 'भाषाएं समर्थित',
    community_stat_pillars: 'AI कोचिंग स्तंभ',
    community_stat_growth: 'दैनिक विकास क्षमता',
    community_stat_privacy: 'गोपनीयता-प्रथम डिज़ाइन',
    consultants_label: '1-ऑन-1 सलाहकार',
    consultants_heading_1: 'एक सत्र बुक करें',
    consultants_heading_2: 'मुस्लिम पेशेवर कोच के साथ',
    consultants_subtext: 'हमारे सत्यापित फ्रीलांस सलाहकार करियर, व्यवसाय, मानसिक स्वास्थ्य और आध्यात्मिक विकास को कवर करने वाले व्यक्तिगत 1-ऑन-1 वीडियो सत्र प्रदान करते हैं — सभी विश्वास-सचेत दृष्टिकोण से।',
    consultants_cta_browse: 'सभी सलाहकार देखें',
    consultants_cta_become: 'सलाहकार बनें',
    consultants_featured_label: 'विशेष सलाहकार',
    consultants_featured_heading: 'अभी बुकिंग के लिए उपलब्ध',
    consultants_available: 'उपलब्ध',
    consultants_busy: 'व्यस्त',
    consultants_sessions: 'सत्र',
    consultants_view_all: 'सभी देखें →',
    ambassadors_label: 'राजदूत कार्यक्रम',
    ambassadors_heading_1: 'कमाएं जबकि आप फैलाते हैं',
    ambassadors_heading_2: 'अपने समुदाय में भलाई',
    ambassadors_subtext: 'रफीक को अपने नेटवर्क के साथ साझा करें और हर उस सदस्यता पर बार-बार कमीशन कमाएं जो आप रेफर करते हैं। जितना आप बढ़ते हैं, आपका स्तर उतना ही ऊंचा होता है — और आपकी कमाई भी।',
    ambassadors_commission: 'कमीशन',
    ambassadors_referrals: 'रेफरल',
    ambassadors_cta_apply: 'अभी आवेदन करें',
    ambassadors_cta_learn: 'और जानें',
    ambassadors_ready_heading: 'क्या आप राजदूत बनने के लिए तैयार हैं?',
    ambassadors_ready_subtext: 'मिनटों में आवेदन करें। कोई न्यूनतम फॉलोअर्स की आवश्यकता नहीं — बस अपने समुदाय को रफीक के साथ बढ़ने में मदद करने का वास्तविक जुनून।',
    seniors_label: 'वरिष्ठों के लिए',
    seniors_heading_1: 'एक सत्यापित विद्वान से पूछें।',
    seniors_heading_2: 'व्यक्तिगत उत्तर पाएं।',
    seniors_subtext: 'हमारी विद्वान निर्देशिका आपको सत्यापित इस्लामी विद्वानों से जोड़ती है जो आपके प्रश्नों का व्यक्तिगत रूप से उत्तर देते हैं — उस धैर्य, सम्मान और गहराई के साथ जिसके वरिष्ठ हकदार हैं।',
    seniors_cta_tour: 'इंटरेक्टिव टूर लें',
    seniors_cta_browse: 'विद्वान निर्देशिका देखें',
    seniors_featured_label: 'विशेष विद्वान',
    seniors_featured_heading: 'हमारे कुछ सत्यापित विद्वानों से मिलें',
    seniors_available: 'उपलब्ध',
    seniors_busy: 'व्यस्त',
    seniors_answers: 'उत्तर',
    seniors_take_tour: 'टूर लें',
    onboarding_label: 'सुरक्षित ऑनबोर्डिंग',
    onboarding_heading: 'आज अपनी यात्रा शुरू करें। आपका डेटा आपका रहता है।',
    onboarding_subtext: '2 मिनट से कम में अपना खाता बनाएं। हम केवल वही एकत्र करते हैं जो हमें आपके AI कोचिंग अनुभव को व्यक्तिगत बनाने के लिए चाहिए — और आप कभी भी सब कुछ हटा सकते हैं।',
    onboarding_cta_create: 'मुफ्त खाता बनाएं',
    onboarding_cta_signin: 'डैशबोर्ड में साइन इन करें',
    onboarding_privacy_note: '🔐 WebAuthn पासकी लॉगिन उपलब्ध · GDPR अनुपालक · शून्य-ज्ञान गोपनीयता',
    footer_features: 'विशेषताएं',
    footer_pricing: 'मूल्य',
    footer_ambassadors: 'राजदूत',
    footer_signin: 'साइन इन',
    lang_switcher_label: 'भाषा',
    lang_reading_in: 'पढ़ रहे हैं',
  },

  bn: {
    nav_features: 'বৈশিষ্ট্য',
    nav_pricing: 'মূল্য',
    nav_all_plans: 'সব পরিকল্পনা',
    nav_community: 'সম্প্রদায়',
    nav_ambassadors: 'রাষ্ট্রদূত',
    nav_consultants: 'পরামর্শদাতা',
    nav_sign_in: 'সাইন ইন',
    nav_get_started: 'শুরু করুন',
    hero_badge: 'AI ইসলামিক ব্যক্তিগত সহকারী ও বৃদ্ধির ইকোসিস্টেম',
    hero_headline_1: 'আপনার AI সঙ্গী',
    hero_headline_highlight: 'দৈনিক বৃদ্ধির',
    hero_headline_2: 'জন্য ইসলামিক জ্ঞানে প্রোথিত',
    hero_subheadline: 'রফিক একটি UK ভিত্তিক প্রযুক্তি কোম্পানি যা বিশ্বের যেকোনো স্থানে মুসলমানদের সেবা করতে চায় — ইতিবাচক মনোবিজ্ঞান, কুরআনিক নীতি এবং রিয়েল-টাইম AI কোচিং একত্রিত করে মুসলিম শিক্ষার্থী, স্নাতক এবং পেশাদারদের তাদের নিজস্ব ভাষায় সামঞ্জস্যপূর্ণ, উদ্দেশ্যমূলক জীবন গড়তে সাহায্য করে।',
    hero_cta_primary: 'আপনার যাত্রা শুরু করুন',
    hero_cta_secondary: 'মূল্য দেখুন',
    hero_download_label: 'অ্যাপ ডাউনলোড করুন:',
    hero_trust_encrypted: 'এন্ড-টু-এন্ড এনক্রিপ্টেড',
    hero_trust_languages: '10টি ভাষা সমর্থিত',
    hero_trust_islamic: 'ইসলামিক-ফার্স্ট ডিজাইন',
    hero_trust_voice: 'রিয়েল-টাইম ভয়েস AI',
    hero_explore: 'অন্বেষণ করুন',
    features_label: 'মূল স্তম্ভ',
    features_heading_1: 'আপনার সম্পূর্ণ সত্তার জন্য তৈরি —',
    features_heading_2: 'মন, ক্যারিয়ার এবং আত্মা',
    features_subtext: 'ছয়টি সমন্বিত মডিউল যা একজন মুসলিম পেশাদারের জীবনের প্রতিটি মাত্রা সমর্থন করতে একসাথে কাজ করে — দৈনন্দিন অভ্যাস থেকে ক্যারিয়ারের সাফল্য পর্যন্ত।',
    pricing_label: 'স্বচ্ছ মূল্য নির্ধারণ',
    pricing_heading: 'একটি প্ল্যাটফর্ম, তিনটি পথ',
    pricing_subtext: 'আপনার জীবনের পর্যায় অনুযায়ী মূল্য নির্ধারিত — আপনার অঞ্চলের উপর ভিত্তি করে ক্রয় ক্ষমতা সমতা স্বয়ংক্রিয়ভাবে প্রয়োগ করা হয়। আপনার স্থানীয় মুদ্রায় মোবাইল মানির মাধ্যমে পেমেন্ট করুন। কোনো লুকানো ফি নেই, যেকোনো সময় বাতিল করুন।',
    pricing_usd_note: 'USD রেফারেন্স মূল্য — নীচে আপনার স্থানীয় মুদ্রায় পেমেন্ট করুন',
    pricing_regional_label: 'মোবাইল মানি দিয়ে পেমেন্ট করুন',
    pricing_regional_heading: 'স্থানীয়ভাবে পেমেন্ট করতে আপনার অঞ্চল নির্বাচন করুন',
    pricing_regional_subtext: 'সমস্ত পেমেন্ট বিশ্বস্ত স্থানীয় মোবাইল মানি প্রদানকারীদের মাধ্যমে প্রক্রিয়া করা হয় — কোনো কার্ডের প্রয়োজন নেই।',
    pricing_pay_here: 'এখানে পেমেন্ট করুন',
    pricing_no_card: '💳 কার্ড চেকআউট উপলব্ধ নয় — সমস্ত পেমেন্ট কম ফি এবং আরও ভালো অ্যাক্সেসিবিলিটির জন্য আঞ্চলিক মোবাইল মানির মাধ্যমে রুট করা হয়।',
    pricing_ppp_note: '🌍 কেনিয়া, উগান্ডা, সোমালিয়া, ইন্দোনেশিয়া, পাকিস্তান এবং মালয়েশিয়ার জন্য স্থানীয় মূল্য নির্ধারণ উপলব্ধ।',
    pricing_then_choose: 'তারপর নীচে আপনার আঞ্চলিক পেমেন্ট পদ্ধতি বেছে নিন ↓',
    community_label: 'বৈশ্বিক সম্প্রদায়',
    community_heading_1: 'মুসলিম ডায়াসপোরার জন্য,',
    community_heading_2: 'আপনি যেখানেই থাকুন',
    community_subtext: 'রফিক সোমালি, আরব, দক্ষিণ এশীয় এবং দক্ষিণ-পূর্ব এশীয় সম্প্রদায়গুলিকে মাথায় রেখে তৈরি করা হয়েছিল — সমস্ত চিন্তার স্কুলে গৃহীত সার্বজনীন ইসলামিক ধারণা ব্যবহার করে। কোনো সাংস্কৃতিক বাধা নেই, শুধু সত্যিকারের সমর্থন।',
    community_cta: 'সম্প্রদায়ে যোগ দিন',
    community_stat_languages: 'ভাষা সমর্থিত',
    community_stat_pillars: 'AI কোচিং স্তম্ভ',
    community_stat_growth: 'দৈনিক বৃদ্ধির সম্ভাবনা',
    community_stat_privacy: 'গোপনীয়তা-প্রথম ডিজাইন',
    consultants_label: '১-অন-১ পরামর্শদাতা',
    consultants_heading_1: 'একটি সেশন বুক করুন',
    consultants_heading_2: 'মুসলিম পেশাদার কোচের সাথে',
    consultants_subtext: 'আমাদের যাচাইকৃত ফ্রিল্যান্স পরামর্শদাতারা ক্যারিয়ার, ব্যবসা, মানসিক সুস্থতা এবং আধ্যাত্মিক বৃদ্ধি কভার করে ব্যক্তিগতকৃত ১-অন-১ ভিডিও সেশন অফার করেন — সবই বিশ্বাস-সচেতন দৃষ্টিভঙ্গি থেকে।',
    consultants_cta_browse: 'সব পরামর্শদাতা দেখুন',
    consultants_cta_become: 'পরামর্শদাতা হন',
    consultants_featured_label: 'বিশেষ পরামর্শদাতা',
    consultants_featured_heading: 'এখন বুকিংয়ের জন্য উপলব্ধ',
    consultants_available: 'উপলব্ধ',
    consultants_busy: 'ব্যস্ত',
    consultants_sessions: 'সেশন',
    consultants_view_all: 'সব দেখুন →',
    ambassadors_label: 'রাষ্ট্রদূত প্রোগ্রাম',
    ambassadors_heading_1: 'ছড়িয়ে দিতে দিতে উপার্জন করুন',
    ambassadors_heading_2: 'আপনার সম্প্রদায়ে ভালো',
    ambassadors_subtext: 'রফিক আপনার নেটওয়ার্কের সাথে শেয়ার করুন এবং আপনি যে প্রতিটি সাবস্ক্রিপশন রেফার করেন তার জন্য পুনরাবৃত্তি কমিশন উপার্জন করুন। আপনি যত বাড়বেন, আপনার স্তর তত উঁচু হবে — এবং আপনার উপার্জনও।',
    ambassadors_commission: 'কমিশন',
    ambassadors_referrals: 'রেফারেল',
    ambassadors_cta_apply: 'এখনই আবেদন করুন',
    ambassadors_cta_learn: 'আরও জানুন',
    ambassadors_ready_heading: 'রাষ্ট্রদূত হতে প্রস্তুত?',
    ambassadors_ready_subtext: 'মিনিটের মধ্যে আবেদন করুন। কোনো ন্যূনতম ফলোয়ার প্রয়োজন নেই — শুধু আপনার সম্প্রদায়কে রফিকের সাথে বাড়তে সাহায্য করার সত্যিকারের আবেগ।',
    seniors_label: 'বয়স্কদের জন্য',
    seniors_heading_1: 'একজন যাচাইকৃত আলেমকে জিজ্ঞাসা করুন।',
    seniors_heading_2: 'ব্যক্তিগত উত্তর পান।',
    seniors_subtext: 'আমাদের আলেম ডিরেক্টরি আপনাকে যাচাইকৃত ইসলামিক আলেমদের সাথে সংযুক্ত করে যারা আপনার প্রশ্নের ব্যক্তিগতভাবে উত্তর দেন — সেই ধৈর্য, সম্মান এবং গভীরতার সাথে যা বয়স্করা প্রাপ্য।',
    seniors_cta_tour: 'ইন্টারেক্টিভ ট্যুর নিন',
    seniors_cta_browse: 'আলেম ডিরেক্টরি দেখুন',
    seniors_featured_label: 'বিশেষ আলেম',
    seniors_featured_heading: 'আমাদের কিছু যাচাইকৃত আলেমের সাথে পরিচিত হন',
    seniors_available: 'উপলব্ধ',
    seniors_busy: 'ব্যস্ত',
    seniors_answers: 'উত্তর',
    seniors_take_tour: 'ট্যুর নিন',
    onboarding_label: 'নিরাপদ অনবোর্ডিং',
    onboarding_heading: 'আজই আপনার যাত্রা শুরু করুন। আপনার ডেটা আপনার থাকে।',
    onboarding_subtext: '২ মিনিটের কম সময়ে আপনার অ্যাকাউন্ট তৈরি করুন। আমরা শুধুমাত্র আপনার AI কোচিং অভিজ্ঞতা ব্যক্তিগতকৃত করতে যা প্রয়োজন তা সংগ্রহ করি — এবং আপনি যেকোনো সময় সবকিছু মুছে ফেলতে পারেন।',
    onboarding_cta_create: 'বিনামূল্যে অ্যাকাউন্ট তৈরি করুন',
    onboarding_cta_signin: 'ড্যাশবোর্ডে সাইন ইন করুন',
    onboarding_privacy_note: '🔐 WebAuthn পাসকি লগইন উপলব্ধ · GDPR সম্মত · জিরো-নলেজ প্রাইভেসি',
    footer_features: 'বৈশিষ্ট্য',
    footer_pricing: 'মূল্য',
    footer_ambassadors: 'রাষ্ট্রদূত',
    footer_signin: 'সাইন ইন',
    lang_switcher_label: 'ভাষা',
    lang_reading_in: 'পড়ছেন',
  },
};

export function getTranslations(language: Language): Translations {
  return translations[language] || translations['en'];
}
